import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Tooltip from '@material-ui/core/Tooltip';
import ErrorIcon from '@material-ui/icons/Error';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import AccessLevelService, { gold } from 'services/AccessLevelService';
import { translate } from 'services/i18nService';
import ProfileStore from 'stores/ProfileStore';
import TippingStore from 'stores/TippingStore';
import UserStore from 'stores/UserStore';
import Button from 'components/Button/Button';
import CustomCheckbox from 'components/CustomCheckbox/CustomCheckbox';
import LoadingButton from 'components/LoadingButton/LoadingButton';
import dates from 'enum/dates';
import { isMyProfileHelperFunc } from 'utils/helpers';

import IconTokens from '../../../../images/profilePage/tokens-grey.png';

import styles from './UserGift.css';


const UserGift = ({
  anonymous,
  close,
  gift,
  giftSendPending,
  handleSendGift,
  hasBalance,
  isDesktop,
  toggleCheckbox,
}) => {
  const { user } = UserStore;
  const { profileName } = ProfileStore;
  const { showBuyTokensModal } = TippingStore;
  const myProfile = isMyProfileHelperFunc(user, profileName);
  const notAdminGift = !gift.adminOnly;
  const ttKey = gift.details && gift.details.ttKey;
  const isGoldUser = AccessLevelService.isMin(gold, user.accessLevel);

  const handleBuyTokens = useCallback(() => {
    close();
    showBuyTokensModal();
  }, [close, showBuyTokensModal]);

  return (
    <div className={styles.UserGift}>
      <img
        alt={gift.name}
        className={styles.giftImage}
        src={gift?.details?.imageUrl}
      />
      <h4>{translate(`gifting.${ttKey}`, {}, gift.name)}</h4>
      <div className={styles.price}>
        <img alt="tokens" className={styles.img} src={IconTokens} />
        {gift.price}
      </div>
      <div className={styles.sender}>
        {gift.sender && gift.sender}{' '}
        {gift.senderType && translate(`v2profile.gift.sender.${gift.senderType.toLowerCase()}`)} (
        {moment(gift.dateSent).format(dates.format.usa)})
      </div>
      {isGoldUser && notAdminGift && !myProfile && (
        <div className={styles.checkboxWrapper}>
          <CustomCheckbox
            checkboxClass={styles.check}
            checked={anonymous}
            className={styles.checkbox}
            disabled={!hasBalance}
            id="send-anonymous-gift"
            label={translate('v2profile.send.as.anonymous')}
            labelClass={styles.label}
            value="checkbox"
            onChange={toggleCheckbox}
          />
          <Tooltip enterTouchDelay={0} title={translate('v2profile.anonymous.gift.tooltip')}>
            <InfoOutlined className={styles.infoIcon} />
          </Tooltip>
        </div>
      )}
      {hasBalance && !myProfile && notAdminGift && (
        <LoadingButton colorVariant="orange" isLoading={giftSendPending} onClick={handleSendGift}>
          {translate('v2profile.send.this.gift')}
        </LoadingButton>
      )}
      {!hasBalance && !myProfile && notAdminGift && isDesktop && (
        <Fragment>
          <div className={styles.notEnoughTokens}>
            <ErrorIcon className={styles.notEnoughTokensIcon} />
            <span>{translate('v2profile.insufficient.balance')}</span>
          </div>
          <Button colour="green" label={translate('v2profile.buy.tokens')} onClick={handleBuyTokens} />
        </Fragment>
      )}
    </div>
  );
};

UserGift.propTypes = {
  close: PropTypes.func.isRequired,
  handleSendGift: PropTypes.func.isRequired,
  toggleCheckbox: PropTypes.func.isRequired,
  anonymous: PropTypes.bool,
  gift: PropTypes.object,
  giftSendPending: PropTypes.bool,
  hasBalance: PropTypes.bool,
  isDesktop: PropTypes.bool,
};

UserGift.defaultProps = {
  anonymous: false,
  gift: undefined,
  giftSendPending: false,
  hasBalance: false,
  isDesktop: false,
};

export default UserGift;
